/**
 * AI CONTEXT: storage - Amplify S3 helpers for product images
 * Journal: J-20250811-02
 *
 * Provides upload and URL resolution helpers using Amplify Storage (v6).
 */

import { uploadData, getUrl } from 'aws-amplify/storage';
import awsConfig from './aws-config';

const PRODUCT_PREFIX = 'products/';
const GIVEAWAY_PREFIX = 'giveaways/';
const ADMIN_SMS_PREFIX = 'sms/';

// Storage requires S3 bucket (public) and an Identity Pool for browser creds.
// Decouple from full Auth configuration to avoid false negatives when User Pool
// envs are not injected but Identity Pool + S3 are present.
export const isStorageConfigured = () =>
  !!process.env.REACT_APP_S3_BUCKET && !!awsConfig?.hasIdentityPool;

export const uploadProductImage = async (file) => {
  if (!isStorageConfigured()) {
    throw new Error('Storage is not configured. Ensure REACT_APP_S3_BUCKET, REACT_APP_AWS_REGION, and REACT_APP_IDENTITY_POOL_ID are set and redeploy.');
  }

  const timestamp = Date.now();
  const sanitized = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const key = `${PRODUCT_PREFIX}${timestamp}_${sanitized}`;

  const result = await uploadData({
    key,
    data: file,
    options: {
      accessLevel: 'public',
      contentType: file.type || 'application/octet-stream',
    },
  }).result;

  // Return a logical reference we can detect later
  return `s3:${result.key}`;
};

export const uploadGiveawayImage = async (file) => {
  if (!isStorageConfigured()) {
    throw new Error('Storage is not configured. Ensure REACT_APP_S3_BUCKET, REACT_APP_AWS_REGION, and REACT_APP_IDENTITY_POOL_ID are set and redeploy.');
  }

  const timestamp = Date.now();
  const sanitized = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const key = `${GIVEAWAY_PREFIX}${timestamp}_${sanitized}`;

  const result = await uploadData({
    key,
    data: file,
    options: {
      accessLevel: 'public',
      contentType: file.type || 'application/octet-stream',
    },
  }).result;

  return `s3:${result.key}`;
};

/**
 * Upload an image intended for admin SMS "picture messages" (AWS-only: link-in-SMS).
 * Returns an `s3:` reference that can be converted to a public URL via `getImageUrlFromKey`.
 */
export const uploadAdminSmsImage = async (file) => {
  if (!isStorageConfigured()) {
    throw new Error('Storage is not configured. Ensure REACT_APP_S3_BUCKET, REACT_APP_AWS_REGION, and REACT_APP_IDENTITY_POOL_ID are set and redeploy.');
  }

  if (!file) {
    throw new Error('No file provided');
  }

  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('Only image files are supported');
  }

  const timestamp = Date.now();
  const rand = Math.floor(1000 + Math.random() * 9000);
  const nameLower = (file.name || '').toLowerCase();
  const extFromName = (nameLower.match(/\.(jpe?g|png|gif|webp)$/) || [])[1];
  const extFromType = (file.type || '').toLowerCase().includes('png')
    ? 'png'
    : (file.type || '').toLowerCase().includes('gif')
      ? 'gif'
      : (file.type || '').toLowerCase().includes('webp')
        ? 'webp'
        : (file.type || '').toLowerCase().includes('jpeg') || (file.type || '').toLowerCase().includes('jpg')
          ? 'jpg'
          : null;
  const ext = extFromName || extFromType || 'jpg';

  // Do not preserve the original file name in the S3 key (looks more "secure"/neutral to clients).
  const key = `${ADMIN_SMS_PREFIX}message_${timestamp}_${rand}.${ext}`;

  const result = await uploadData({
    key,
    data: file,
    options: {
      accessLevel: 'public',
      contentType: file.type || 'application/octet-stream',
    },
  }).result;

  return `s3:${result.key}`;
};

export const getImageUrlFromKey = async (s3KeyOrRef) => {
  if (!s3KeyOrRef) return null;
  const key = s3KeyOrRef.startsWith('s3:') ? s3KeyOrRef.slice(3) : s3KeyOrRef;

  // First try the public API endpoint (works for non-authenticated users)
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/images/public?key=${encodeURIComponent(key)}`;
    const response = await fetch(apiUrl);
    if (response.ok) {
      const data = await response.json();
      if (data.url) {
        return data.url;
      }
    }
  } catch (apiError) {
    console.warn('Public API failed, falling back to authenticated method:', apiError);
  }

  // Fallback to authenticated Amplify method (requires AWS credentials)
  try {
    const { url } = await getUrl({ key, options: { accessLevel: 'public', validateObjectExistence: false } });
    return url.toString();
  } catch (_e) {
    return null;
  }
};


